import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import filterSwitch from '@/components/panel/haggle-event/utils/filterSwitch';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import HaggleAdminMonthlySalesReportTable from '@/components/table/haggle-event/HaggleAdminMonthlySalesReportTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import {
	calculateStatus,
	formatHaggleEventDate,
	formatPrice,
	formatToUrlDate,
	isValidDateString,
} from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import {
	IHaggleEventMonthlySalesReport,
	IHaggleEventSalesReport,
} from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const AdminReportsDashboard = () => {
	const { date: dateParam } = useParams<{ date?: string }>();

	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchQuery, setSearchQuery] = useState('');
	const [filterSelected, setFilterSelected] = useState('');
	const [selectedPickerDate, setSelectedPickerDate] = useState<Date>(
		new Date(),
	);
	const navigate = useNavigate();
	const reportDateString = formatToUrlDate(selectedPickerDate);

	const handleDateChange = (newDate: Date | null) => {
		if (newDate) {
			setSelectedPickerDate(newDate); // Update the picker state first
			const newDateString = formatToUrlDate(newDate); // Format to 'YYYY-MM-01'

			// Navigate to the new date URL, triggering useEffect and query refetch
			navigate(`/user/reports/${newDateString}`, { replace: true });
		}
	};

	useEffect(() => {
		if (dateParam && isValidDateString(dateParam)) {
			// If URL date is valid, set the picker date
			// Add 'T00:00:00' to parse correctly regardless of timezone
			const urlDate = new Date(dateParam + 'T00:00:00');
			if (
				urlDate.getFullYear() !== selectedPickerDate.getFullYear() ||
				urlDate.getMonth() !== selectedPickerDate.getMonth()
			) {
				setSelectedPickerDate(urlDate);
			}
		} else {
			// If URL date is missing or invalid, navigate to the current month's report URL
			const currentMonthDateString = formatToUrlDate(new Date());
			navigate(`/user/reports/${currentMonthDateString}`, {
				replace: true,
			});
			setSelectedPickerDate(new Date());
		}
	}, [dateParam, navigate]);

	const filter = {
		'page[size]': pageSize,
		'page[number]': pageNumber,
		...(searchQuery && { 'filterQuery[findByIdOrTradeName]': searchQuery }),
		...(filterSelected && filterSwitch(filterSelected)),
	};

	const {
		data: haggleEvents,
		isLoading: haggleEventIsLoading,
		isError: haggleEventIsError,
		error: haggleEventError,
		refetch: haggleEventRefetch,
	} = useQuery<IHaggleEventMonthlySalesReport>({
		queryKey: [
			ApiQueryKeys.getAllHaggleEventsAdmin,
			pageSize,
			pageNumber,
			reportDateString,
		],
		queryFn: () =>
			haggleApiService.getHaggleEventMonthlySalesReportAdmin(reportDateString, {
				params: filter,
			}),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		haggleEventRefetch();
	}, [filterSelected, haggleEventRefetch]);

	const { data: merchants } = useQuery({
		queryKey: [ApiQueryKeys.getAllMerchants],
		queryFn: () => haggleApiService.getAllMerchants(),
		refetchOnWindowFocus: false,
	});

	if (haggleEventIsError) {
		if (haggleEventError instanceof Error) {
			notificationService.error(haggleEventError.message);
		} else {
			notificationService.error('Unknown error fetching haggle events');
		}
	}

	const handlePaginationOptions = (
		collection: IHaggleEventMonthlySalesReport,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const columnHelper = createColumnHelper<IHaggleEventSalesReport>();

	const columns = [
		columnHelper.accessor('store.tradeName', {
			cell: (info) => <div className="w-[150px]">{info.getValue()}</div>,
			header: () => <div className="w-[150px]">STORE NAME</div>,
		}),
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
			header: () => <div className="w-[50px]">ID</div>,
		}),
		{
			id: 'status',
			cell: (info: { row: { original: IHaggleEventSalesReport } }) => {
				const haggleEvent = info.row.original;
				return (
					<div className="w-[70px]">{`${calculateStatus(
						haggleEvent.endDate,
						haggleEvent.startDate,
					)}`}</div>
				);
			},
			header: () => <div className="w-[70px]">STATUS</div>,
		},
		columnHelper.accessor('startDate', {
			cell: (info) => (
				<div className="w-[170px]">
					{formatHaggleEventDate(
						info.row.original.startDate,
						info.row.original.endDate,
					)}
				</div>
			),
			header: () => <div className="w-[170px]">DATE</div>,
		}),
		columnHelper.accessor('agreements', {
			cell: (info) => {
				const agreementsReached = info.getValue()?.length;
				const totalDeals = info
					.getValue()
					?.filter((agreement) => agreement.isSold).length;
				return (
					<div className="w-[120px] text-center">{`${totalDeals}/${agreementsReached}`}</div>
				);
			},
			header: () => <div className="w-[120px] text-center">SALES/DEALS</div>,
		}),
		columnHelper.accessor('acceptableMargin', {
			cell: (info) => (
				<div className="w-[80px] text-center">{`${info.getValue()} %`}</div>
			),
			header: () => <div className="w-[80px] text-center">MAX. DISCOUNT</div>,
		}),
		columnHelper.accessor('totalSalesMade', {
			cell: (info) => (
				<div className="w-[120px] text-center">{`${formatPrice(
					info.getValue() ?? '0',
				)}`}</div>
			),
			header: () => <div className="w-[120px] text-center">TOTAL SALES</div>,
		}),
		columnHelper.accessor('totalProfit', {
			cell: (info) => (
				<div className="w-[210px] text-center">{`${formatPrice(
					info.getValue() ?? '0',
				)}`}</div>
			),
			header: () => (
				<div className="w-[210px] text-center">ADDED PROFIT MARGIN</div>
			),
		}),
		columnHelper.accessor('productIds', {
			cell: (info) => (
				<div className="w-[100px] text-center">{`${
					info.getValue().length
				}`}</div>
			),
			header: () => <div className="w-[100px]">PRODUCTS</div>,
		}),
	];

	return (
		<div
			className="min-h-content overflow-y-auto"
			data-test="haggle-events-admin-dashboard"
		>
			{haggleEventIsLoading && <LoadingSkeletonModalTable />}
			{haggleEvents && !haggleEventIsLoading && (
				<div className="flex flex-col h-full grow gap-4">
					<HaggleAdminMonthlySalesReportTable
						collectionHaggleEvent={haggleEvents}
						paginationOption={handlePaginationOptions(haggleEvents)}
						columns={columns}
						merchants={merchants ? merchants.itemCount : NaN}
						refetch={haggleEventRefetch}
						query={searchQuery}
						setQuery={setSearchQuery}
						setFilterSelected={setFilterSelected}
						handleDateChange={handleDateChange}
						selectedPickerDate={selectedPickerDate}
						reportDateString={reportDateString}
						totalSalesMade={haggleEvents.data.totalSalesMade}
						totalProfit={haggleEvents.data.totalProfit}
						totalDeals={haggleEvents.data.totalDeals}
					/>
				</div>
			)}
		</div>
	);
};

export default AdminReportsDashboard;
